import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Clock, LogIn, LogOut, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInOut = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchTodayAttendance();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/today-attendance`, {
        headers: getAuthHeader()
      });
      setTodayRecord(response.data);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/employee/checkin`,
        {},
        { headers: getAuthHeader() }
      );
      
      alert(`✓ ${response.data.message}\nTime: ${response.data.check_in_time}`);
      await fetchTodayAttendance();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/employee/checkout`,
        {},
        { headers: getAuthHeader() }
      );
      
      alert(`✓ ${response.data.message}\nCheck-in: ${response.data.check_in_time}\nCheck-out: ${response.data.check_out_time}`);
      await fetchTodayAttendance();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to check out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Attendance Check-In/Out</h2>

      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="text-primary" size={24} />
            Today's Attendance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-6xl font-bold text-primary mb-4">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </div>
            <div className="text-gray-600 mb-8">
              {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>

            {!todayRecord?.check_in ? (
              <Button
                onClick={handleCheckIn}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-12 h-16"
              >
                <LogIn size={24} className="mr-3" />
                Check In
              </Button>
            ) : !todayRecord?.check_out ? (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Clock size={20} />
                    <span className="font-medium">Checked in at {todayRecord.check_in}</span>
                  </div>
                </div>
                <Button
                  onClick={handleCheckOut}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white text-lg px-12 h-16"
                >
                  <LogOut size={24} className="mr-3" />
                  Check Out
                </Button>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="text-gray-700 space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <LogIn size={20} className="text-green-600" />
                    <span>Checked in: <strong>{todayRecord.check_in}</strong></span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <LogOut size={20} className="text-red-600" />
                    <span>Checked out: <strong>{todayRecord.check_out}</strong></span>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    ✓ Attendance marked for today
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Check in when you start your work day</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Check out when you finish your work day</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Your attendance is automatically tracked and recorded</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>View your complete attendance history in the Attendance section</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckInOut;
